import { FolderPlus, Search } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Folder } from "~/api/model";
import { Loader } from "~/components/Loader";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { ROUTES, useOrganizationParams } from "~/helpers";
import { useFtue } from "~/hooks/useFtue";
import { CreateCaseDialog } from "./CreateCaseDialog";

interface FtueWelcomeProps {
  demoCase?: Folder;
  isDemoCaseLoading?: boolean;
}

export const FtueWelcome: FC<FtueWelcomeProps> = ({
  demoCase,
  isDemoCaseLoading,
}) => {
  const navigate = useNavigate();
  const { organizationId } = useOrganizationParams();
  const { user, completeFtue, isCompletingFtue, trackFtueEvent } = useFtue();
  const [hasTrackedView, setHasTrackedView] = useState(false);

  useEffect(() => {
    if (!hasTrackedView) {
      trackFtueEvent("ftue_welcome_viewed");
      setHasTrackedView(true);
    }
  }, [hasTrackedView, trackFtueEvent]);

  const firstName = user?.name?.split(" ")[0] || "";

  const handleExploreDemoCase = async () => {
    trackFtueEvent("ftue_explore_demo_clicked");
    await completeFtue("explore_demo");
    if (demoCase) {
      navigate(ROUTES.CASE({ organizationId, caseId: demoCase.id }));
    }
  };

  const handleSkip = async () => {
    trackFtueEvent("ftue_skipped");
    await completeFtue("skip");
  };

  const handleCreateCaseClick = () => {
    trackFtueEvent("ftue_create_case_clicked");
    completeFtue("create_case");
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-4">
      <div className="max-w-2xl w-full text-center space-y-8">
        <div className="space-y-3">
          <h1 className="text-3xl font-semibold text-gray-900">
            <FormattedMessage
              id="ftue.welcome.title"
              defaultMessage="Welcome to Sky AI, {firstName}!"
              values={{ firstName }}
            />
          </h1>
          <p className="text-lg text-gray-600">
            <FormattedMessage
              id="ftue.welcome.subtitle"
              defaultMessage="Get your first case organized in minutes."
            />
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CreateCaseDialog
            isFirstCase={true}
            TriggerButton={
              <Card
                className="cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                onClick={handleCreateCaseClick}
              >
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <FolderPlus className="w-6 h-6 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg">
                    <FormattedMessage
                      id="ftue.welcome.createCase"
                      defaultMessage="Create Your First Case"
                    />
                  </CardTitle>
                  <CardDescription>
                    <FormattedMessage
                      id="ftue.welcome.createCaseDescription"
                      defaultMessage="Upload documents and let Sky AI organize them for you."
                    />
                  </CardDescription>
                </CardHeader>
              </Card>
            }
          />

          {demoCase ? (
            <Card
              className="cursor-pointer hover:border-purple-500 hover:shadow-md transition-all"
              onClick={handleExploreDemoCase}
            >
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Search className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-lg">
                  <FormattedMessage
                    id="ftue.welcome.exploreDemo"
                    defaultMessage="Explore Demo Case"
                  />
                </CardTitle>
                <CardDescription>
                  <FormattedMessage
                    id="ftue.welcome.exploreDemoDescription"
                    defaultMessage="See how Sky AI works with sample documents."
                  />
                </CardDescription>
              </CardHeader>
            </Card>
          ) : isDemoCaseLoading ? (
            <Card className="border-dashed border-purple-200 bg-purple-50/50">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Loader size={24} color="#9333ea" thickness={2} />
                </div>
                <CardTitle className="text-lg text-purple-700">
                  <FormattedMessage
                    id="ftue.welcome.demoCaseLoading"
                    defaultMessage="Setting up your demo case..."
                  />
                </CardTitle>
                <CardDescription>
                  <FormattedMessage
                    id="ftue.welcome.demoCaseLoadingDescription"
                    defaultMessage="This will only take a moment."
                  />
                </CardDescription>
              </CardHeader>
            </Card>
          ) : null}
        </div>

        {demoCase && (
          <div>
            <Button
              variant="link"
              onClick={handleSkip}
              disabled={isCompletingFtue}
              className="text-gray-500 hover:text-gray-700"
            >
              <FormattedMessage
                id="ftue.welcome.skip"
                defaultMessage="Skip to dashboard"
              />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
